
import { Box, BoxProps, Button, Divider, IconButton, Link, LinkProps, Paper, Table, TableBody, TableCell, TableContainer, TableRow, Typography, styled } from '@mui/material';
import { FC, useContext } from 'react';
import { faMinus, faPlus, faUser, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { faUser as faUserClosed } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { grey } from '@mui/material/colors';
import CommonContext from '../../contexts/data/CommonContext';
import Hr from './HorizontalRule';
import AuthContext from '../../contexts/auth/AuthContext';
import { useParams } from 'react-router-dom';
import { SpeciesContext } from '../../contexts/data/SpeciesContext';
import { Species } from '../../api/dtos/dtos';
import Center from './Center';
import FormButton from '../map/FormButton';
import FilterButton from '../map/FilterButton';


const HelpContainer = styled(Box)(() => ({
    backgroundColor: 'white',
    padding: '15px',
    borderRadius: '4px',
    overflow: 'auto',
    maxHeight: '90vh',
    margin: 'auto',
}));

const StyledFooterButton = styled(Button)(({ theme }) => ({
    maxWidth: '100px',
    maxHeight: '40px',
    backgroundColor: 'transparent',
    border: `3px solid ${theme.palette.primary.main}`,
    color: theme.palette.primary.main,
    marginLeft: '30px',

    "&:hover": {
        backgroundColor: 'transparent',
        boxShadow: '0px 0px 6px 1px rgba(75,90,35,0.5)'
    },

    "&:active": {
        backgroundColor: theme.palette.secondary.main,
        color: 'white'
    }
}));

const StyledHeaderContainer = styled(Box)(({theme}) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: theme.palette.primary.main,
    fontSize: 24,
    fontWeight: 500,
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
    maxHeight: '40px', 
    maxWidth: '40px',
    backgroundColor: 'white',
    margin: '0 6px',
    color: theme.palette.primary.main,

    "&:hover": {
        backgroundColor: grey[200],
        boxShadow: '0 0px 3px 2px rgba(0,0,0,0.12), 0 0px 2px rgba(0,0,0,0.24)'
    },

    "&:active": {
        backgroundColor: theme.palette.secondary.main,
        color: 'white'
    }
}));

const StyledFooterContainer = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'end',
    margin: '15px 0'
}));

const StyledProfileIconButton = styled(IconButton)(({ theme }) => ({
    maxHeight: '40px', 
    maxWidth: '40px',
    backgroundColor: theme.palette.primary.main,
    margin: '0 6px',

    "&:hover": {
        backgroundColor: theme.palette.primary.main,
        boxShadow: '0 0px 3px 2px rgba(0,0,0,0.12), 0 0px 2px rgba(0,0,0,0.24)'
    },

    "&:active": {
        backgroundColor: theme.palette.secondary.main
    }
}));

const CenteredContainer = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
}));

const RecordSpeciesLink = styled(Box)<BoxProps & LinkProps>(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    textAlign: 'center',
    top: '100%',
    backgroundColor: theme.palette.primary.main,
    cursor: 'pointer',

    textDecoration: 'none',
    lineHeight: '3rem',
    fontSize: '1.25rem',
    color: 'white',
    borderRadius: '30px',

    boxShadow: 'inset 0 0 2px rgba(50, 50, 50, 0.6)',
    width: '100%',
    height: '48px',

    "&:hover": {
        WebkitBoxShadow: '0px 0px 8px 2px rgba(75,90,35,0.5)',
        MozBoxShadow: '0px 0px 8px 2px rgba(75,90,35,0.5)',
        boxShadow: '0px 0px 8px 2px rgba(75,90,35,0.5)'
    }
}));

const ZoomWrapper = styled(Box)(() => ({
    width: '40px',
    height: '80px',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '5px',
    backgroundColor: 'white',
    justifyContent: 'space-around',
    overflow: 'hidden',
    color: 'grey',
    boxShadow: '0px 0px 6px 1px rgba(75,90,35,0.5)'
}));

const StyledCenterZoom = styled(Center)(({ theme }) => ({
    height: '100%',

    "&:hover": {
        color: theme.palette.primary.main,
        cursor: 'pointer'
    }
}));

const LinkContainer = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column'
}));

const HelpModal: FC = () => {

    const { user } = useContext(AuthContext);
    const { setHelpOpen } = useContext(CommonContext);
    const { species: { species }} = useContext(SpeciesContext);
    const { species: speciesName } = useParams();

    const getSpeciesPlurals = (species: Species) => {
        switch (species) {
            case Species.Rabbit:
                return "Rabbits";
            case Species.Fox:
                return "Foxes";
            case Species.Camel:
                return "Feral Camels";
            case Species.FeralPig:
                return "Feral Pigs";
            case Species.Myna:
                return "Myna Birds";
            case Species.WildDog:
                return "Wild Dogs";
            case Species.Toad:
                return "Toads";
            case Species.FeralGoat:
                return "Feral Goats";
            case Species.Starling:
                return "Starlings";
            case Species.Deer:
                return "Deer";
            case Species.FeralFish:
                return "Feral Fish";
            case Species.Mouse:
                return "Mice";
            case Species.FeralCat:
                return "Feral Cats";
            case Species.NewPests:
                return "New Pests";
            case Species.OtherPests:
                return "Other Pests";
            default:
                return "";
        }
    }

    return (
        <HelpContainer onClick={(e) => { e.stopPropagation() }}>
            {/* Header container */}
            <StyledHeaderContainer>
                <StyledHeaderContainer>Help</StyledHeaderContainer>
                <StyledIconButton onClick={() => setHelpOpen(false)}>
                    <FontAwesomeIcon icon={faXmark} />
                </StyledIconButton>
            </StyledHeaderContainer>

            <Hr color={grey[200]}/>

            {/* Title */}
            <Typography color={'black'} paddingBottom={'24px'}>FeralScan is a community resource to help people monitor, map and manage pest animals.</Typography>

            {/* Table */}
            <TableContainer component={Paper}>
                <Table aria-label="help-table">
                    <TableBody>
                        <TableRow>
                            <TableCell>
                                <CenteredContainer>
                                    <StyledProfileIconButton 
                                        disableRipple
                                        onClick={() => null}
                                    >
                                        {
                                            user ? 
                                                <FontAwesomeIcon color='white' fontSize='1.2rem' icon={faUserClosed} /> :
                                                <FontAwesomeIcon color='white' fontSize='1.2rem' icon={faUser} />

                                        }
                                    </StyledProfileIconButton>
                                </CenteredContainer>
                            </TableCell>
                            <TableCell>
                                <CenteredContainer>
                                    <Typography>Login/Register as a new user.</Typography>
                                    <Typography>Forgot your password? <Link sx={{ cursor: 'pointer' }} href={ !user ? `/${speciesName}/forgotpassword` : undefined}>Click here</Link></Typography>
                                </CenteredContainer>
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>
                                <CenteredContainer>
                                    <RecordSpeciesLink>
                                        Record {getSpeciesPlurals(species)}
                                    </RecordSpeciesLink>
                                </CenteredContainer>
                            </TableCell>
                            <TableCell>
                                <CenteredContainer>
                                    <Typography>Open the map</Typography>
                                </CenteredContainer>
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>
                                <CenteredContainer>
                                    <ZoomWrapper>
                                        <StyledCenterZoom>
                                            <FontAwesomeIcon fontSize="1.2rem" icon={faPlus} />
                                        </StyledCenterZoom>
                                        <Divider />
                                        <StyledCenterZoom>
                                            <FontAwesomeIcon fontSize="1.2rem" icon={faMinus} />
                                        </StyledCenterZoom>
                                    </ZoomWrapper>
                                </CenteredContainer>
                            </TableCell>
                            <TableCell>
                                <CenteredContainer>
                                    <Typography>Zoom in/out</Typography>
                                </CenteredContainer>
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>
                                <CenteredContainer>
                                    <FormButton />
                                </CenteredContainer>
                            </TableCell>
                            <TableCell>
                                <CenteredContainer>
                                    <Typography>Add a new record, such as a sighting, photograph, evidence, pest impact or control activity.</Typography>
                                </CenteredContainer>
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>
                                <CenteredContainer>
                                    <FilterButton 
                                        loading={false}
                                        disabled={false}
                                    />
                                </CenteredContainer>
                            </TableCell>
                            <TableCell>
                                <CenteredContainer>
                                    <Typography>Select the data you wish to view on the map, such as records from your private group.</Typography>
                                </CenteredContainer>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
            
            <br />

            {/* Links */}
            <LinkContainer>
                <Link href={`/${speciesName}/content/${speciesName?.replace("scan", "")?.replace("alert", "")}_howtousethewebsite`}>Learn more about How to use the website</Link>
                <Link href={`/${speciesName}/content/${speciesName?.replace("scan", "")?.replace("alert", "")}_howtousetheapp`}>Learn more about How to use the App</Link>
                <Link href={`/${speciesName}/content/${speciesName?.replace("scan", "")?.replace("alert", "")}_contacts`}>Contact us for assistance</Link>
            </LinkContainer>
            <br />
            <Hr color={grey[200]}/>

            {/* Footer */}
            <StyledFooterContainer>
                <StyledFooterButton
                    onClick={() => setHelpOpen(false)}
                >
                    <Typography>Okay</Typography>
                </StyledFooterButton>
            </StyledFooterContainer>
        </HelpContainer>
    )
}

export default HelpModal